import type { AnonymousFixture, FixtureElement } from "./types";

const elementTypes = ["Wall", "Door", "Slab", "Beam", "Proxy"] as const;

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value)) {
      deepFreeze(child);
    }
  }
  return value;
}

function createElement(index: number): FixtureElement {
  const type = elementTypes[index % elementTypes.length];
  const isUnsupported = type === "Proxy" && index % 3 === 0;
  const hasClassification = index % 11 !== 0;
  const hasFireRating = index % 4 === 0;
  const gridWidth = 24;
  const column = index % gridWidth;
  const row = Math.floor(index / gridWidth) % 15;
  const level = Math.floor(index / (gridWidth * 15));

  return deepFreeze({
    id: `anon-${String(index + 1).padStart(5, "0")}`,
    label: `${type} ${String(index + 1).padStart(4, "0")}`,
    type,
    properties: hasFireRating
      ? { FireRating: `${30 + (index % 3) * 30}min`, Manufacturer: `Sample ${index % 7}` }
      : { Manufacturer: `Sample ${index % 7}` },
    quantities: { Area: Number((8 + (index % 19) * 0.75).toFixed(2)), Volume: 2 + (index % 9) },
    classification: hasClassification
      ? { system: "Uniclass 2015", code: `Pr_${String(20 + (index % 70)).padStart(2, "0")}`, label: type }
      : null,
    geometry: {
      kind: "box",
      position: [column * 1.4, level * 3, row * 1.4] as const,
      size: [0.9, type === "Slab" ? 0.25 : 2.2, 0.9] as const
    },
    editable: index % 13 !== 0,
    ...(isUnsupported ? { unsupportedReason: "Fixture proxy has no supported mapping adapter." } : {}),
    unknown: {
      producerExtension: { opaqueKey: `x-${index % 5}`, value: `opaque-${index}` },
      sourceNote: index % 2 === 0 ? "Retained verbatim" : null
    }
  });
}

export function createAnonymousFixture(elementCount = 360): AnonymousFixture {
  if (!Number.isInteger(elementCount) || elementCount < 0) {
    throw new RangeError("elementCount must be a non-negative integer.");
  }

  return deepFreeze({
    id: "d0-anonymous-fixture-v1",
    label: "Anonymous D0 sample",
    elements: Array.from({ length: elementCount }, (_, index) => createElement(index))
  });
}

export const anonymousFixture = createAnonymousFixture();
